/**
 * EventLog - Scrollable event history for proctoring test
 *
 * Displays chronological log of all proctoring events:
 * - Enrollment/verification attempts
 * - Warnings (no face, identity mismatch)
 * - Errors (API failures, suspensions)
 * - Info messages (state changes)
 */

// Event types for the log
export interface ProctoringEvent {
  id: string
  timestamp: number
  type: 'success' | 'warning' | 'error' | 'info'
  message: string
  details?: string
}

interface EventLogProps {
  events: ProctoringEvent[]
  onClear: () => void
}

export default function EventLog({ events, onClear }: EventLogProps) {
  return (
    <div className="bg-gray-800 rounded-xl p-4 h-full flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold">Event Log</h2>
        <button
          onClick={onClear}
          className="px-3 py-1 text-sm bg-gray-700 hover:bg-gray-600 rounded transition-colors"
        >
          Clear
        </button>
      </div>

      {/* Event List */}
      <div className="flex-1 overflow-y-auto space-y-2 min-h-0">
        {events.length === 0 ? (
          <div className="text-gray-500 text-center py-8">
            No events yet. Start verification to see activity.
          </div>
        ) : (
          events.map((event) => (
            <EventItem key={event.id} event={event} />
          ))
        )}
      </div>

      {/* Event Count */}
      {events.length > 0 && (
        <div className="text-xs text-gray-500 mt-2 text-right">
          {events.length} event{events.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  )
}

// Individual event display
function EventItem({ event }: { event: ProctoringEvent }) {
  const iconAndColor = {
    success: { icon: '✓', bg: 'bg-green-900/30', text: 'text-green-400', border: 'border-green-700' },
    warning: { icon: '⚠', bg: 'bg-yellow-900/30', text: 'text-yellow-400', border: 'border-yellow-700' },
    error: { icon: '✕', bg: 'bg-red-900/30', text: 'text-red-400', border: 'border-red-700' },
    info: { icon: 'ℹ', bg: 'bg-blue-900/30', text: 'text-blue-400', border: 'border-blue-700' },
  }[event.type]

  const time = new Date(event.timestamp).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })

  return (
    <div className={`p-2 rounded-lg border ${iconAndColor.bg} ${iconAndColor.border}`}>
      <div className="flex items-start gap-2">
        {/* Icon */}
        <span className={`${iconAndColor.text} text-sm flex-shrink-0 w-5 text-center`}>
          {iconAndColor.icon}
        </span>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className={`${iconAndColor.text} text-sm font-medium truncate`}>
              {event.message}
            </span>
            <span className="text-xs text-gray-500 flex-shrink-0">
              {time}
            </span>
          </div>

          {event.details && (
            <div className="text-xs text-gray-400 mt-0.5 truncate">
              {event.details}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
